import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import "./Dashboard.scss";
import Loader from "../../Components/Loader/Loader";
import { getApi } from "../../Utlis/apiService";
import {
  GET_ALL_DASHBOARD_DATA,
  GET_ALL_DASHBOARD_DATA_OFFLINE,
} from "../../Utlis/apiPath";
import { DatePicker } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { Dropdown, Space } from "antd";
import dayjs, { Dayjs } from "dayjs";

const { RangePicker } = DatePicker;

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all"); 
  const [dateRange, setDateRange] = useState([]); 
  const [visible, setVisible] = React.useState(false);
  const [panelVisible, setPanelVisible] = React.useState(false);
  const [dates, setDates] = React.useState(() => [
    dayjs(),
    dayjs().add(1, "day"),
  ]);

  const mapOfflineData = (data) => {
    return {
      totalClassBooking: data?.totalOfflineBooking, 
      totalRevenue: data?.totalRevenue,
      subscriptionEarnings: data?.subscriptionEarnings,
      oneTimeEarnings: data?.oneTimeEarnings,
      revenueVsTime: data?.revenueVsTime,
      bookingCountVsTime: data?.bookingCountVsTime,
      topBookedClasses: undefined,
      subscribersOverTime: undefined,
      averageBookingValue: data?.averageBookingValue,
      averageClassBookingCount: data?.averageClassBookingCount,
      topInstructors: data?.topBookedInstructors
        ? data?.topBookedInstructors.map((item) => ({
            name: `${item.first_name} ${item.last_name}`,
            bookings: Number(item.count),
          }))
        : [],
    };
  };

  const fetchData = async (tabType = "online") => {
    setIsLoading(true);
    try {
      const params =
        dateRange && dateRange[0] && dateRange[1]
          ? {
              params: {
                startDate: dateRange[0].format("YYYY-MM-DD"),
                endDate: dateRange[1].format("YYYY-MM-DD"),
              },
            }
          : undefined;

      let response;
      if (tabType === "offline") {
        response = await getApi(GET_ALL_DASHBOARD_DATA_OFFLINE, params);
      } else if (tabType === "online") {
        response = await getApi(GET_ALL_DASHBOARD_DATA, params);
      } else {
        const [onlineResponse, offlineResponse] = await Promise.all([
          getApi(GET_ALL_DASHBOARD_DATA, params),
          getApi(GET_ALL_DASHBOARD_DATA_OFFLINE, params),
        ]);

        if (onlineResponse && offlineResponse) {
          const onlineData = onlineResponse.data;
          const offlineData = mapOfflineData(offlineResponse?.data);
          // console.log(onlineData, "onlineData");
          // console.log(offlineData, "offlineData");
          const combinedData = {
            totalClassBooking: {
              count:
                Number(onlineData?.totalClassBooking?.count || 0) +
                Number(offlineData?.totalClassBooking?.count || 0),
            },

            totalRevenue: {
              count:
                Number(onlineData?.totalRevenue?.count || 0) +
                Number(offlineData?.totalRevenue?.count || 0),
            },
            // For now, show online data for charts that don't exist in offline
            revenueVsTime: onlineData?.revenueVsTime,
            bookingCountVsTime: onlineData?.bookingCountVsTime,
            topBookedClasses: onlineData?.topBookedClasses,
            subscribersOverTime: onlineData?.subscribersOverTime,
            // Combine average booking value from both sources
            averageBookingValue: {
              amount:
                Number(onlineData?.averageBookingValue?.amount || 0) +
                Number(offlineData?.averageBookingValue?.amount || 0),
            },
            // Combine average class booking count from both sources
            averageClassBookingCount:
              Number(onlineData?.averageClassBookingCount || 0) +
              Number(offlineData?.averageClassBookingCount || 0),
            topInstructors: [
              ...(onlineData?.topBookedInstructors || []).map((item) => ({
                name: `${item.first_name} ${item.last_name}`,
                bookings: Number(item.count),
              })),
              ...(offlineData?.topInstructors || []),
            ],
          };

          setDashboardData(combinedData);
          setIsLoading(false);
          return;
        } else {
          response = onlineResponse || offlineResponse;
        }
      }

      if (!response) {
        throw new Error("No response received from API");
      }
      let data = response.data;
      if (tabType === "offline") {
        data = mapOfflineData(data);
      } else {
        // Map topBookedInstructors to topInstructors for online data
        data.topInstructors = Array.isArray(data.topBookedInstructors)
          ? data.topBookedInstructors.map((item) => ({
              name: `${item.first_name} ${item.last_name}`,
              bookings: Number(item.count),
            }))
          : [];
      }
      setDashboardData(data);
    } catch (error) {
      console.error("🚨 API call error:", error);
      setError(error.message);
      setDashboardData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dateRange]);

  // const handleDateChange = (dates) => {
  //   // dates may be null when cleared via allowClear
  //   setDateRange(dates || []);
  // };

  // const [dateRange, setDateRange] = useState([]);

  const handleDateChange = (dates) => {
    setDateRange(dates || []);
  };

  // const handleDateChange = (dates) => {
  //   // dates may be null when cleared via allowClear
  //   setDateRange(dates || []);
  //   setVisible(false);
  //   setPanelVisible(false);
  // };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (isLoading) {
    return (
      <div className="dashboard-loader">
        <Loader size={50} />
      </div>
    );
  }

  const timeLabels = [
    { label: "15 Days", key: "last15DaysRevenue" },
    { label: "30 Days", key: "last30DaysRevenue" },
    { label: "45 Days", key: "last45DaysRevenue" },
    { label: "60 Days", key: "last60DaysRevenue" },
    { label: "75 Days", key: "last75DaysRevenue" },
    { label: "90 Days", key: "last90DaysRevenue" },
  ];

  const timeLabels2 = [
    { label: "15 Days", key: "last15DaysBookingCount" },
    { label: "30 Days", key: "last30DaysBookingCount" },
    { label: "45 Days", key: "last45DaysBookingCount" },
    { label: "60 Days", key: "last60DaysBookingCount" },
    { label: "75 Days", key: "last75DaysBookingCount" },
    { label: "90 Days", key: "last90DaysBookingCount" },
  ];

  const revenueChartData = timeLabels.map(({ label, key }) => ({
    time: label,
    revenue: Number(dashboardData?.revenueVsTime?.[key] || 0),
  }));

  const bookingChartData = timeLabels2.map(({ label, key }) => ({
    time: label,
    bookings: Number(dashboardData?.bookingCountVsTime?.[key] || 0),
  }));

  const topClassesData =
    dashboardData?.topBookedClasses?.map((item) => ({
      name: item.title,
      bookings: Number(item.count),
    })) || [];

  const topInstructorsData =
    dashboardData?.topInstructors?.map((item) => ({
      name: item.name, // FIX: use 'name' instead of 'instructorName'
      bookings: Number(item.bookings ?? item.count),
    })) || [];

  const subscribersOverTimeData = Object.keys(
    dashboardData?.subscribersOverTime || {}
  ).map((key) => ({
    time: key.replace("DaysBookingCount", " Days"), // Add space and remove 'Booking'
    subscribers: Number(dashboardData?.subscribersOverTime[key] || 0),
  }));

  const menuItems = [
    {
      key: "today",
      label: "Today",
      onClick: () => handleDateChange([dayjs(), dayjs()]),
    },
    {
      key: "yesterday",
      label: "Yesterday",
      onClick: () =>
        handleDateChange([
          dayjs().subtract(1, "day"),
          dayjs().subtract(1, "day"),
        ]),
    },
    {
      key: "this-week",
      label: "This Week",
      onClick: () =>
        handleDateChange([dayjs().startOf("week"), dayjs().endOf("week")]),
    },
    {
      key: "last-week",
      label: "Last Week",
      onClick: () =>
        handleDateChange([
          dayjs().subtract(1, "week").startOf("week"),
          dayjs().subtract(1, "week").endOf("week"),
        ]),
    },
    {
      key: "last-7",
      label: "Last 7 days",
      onClick: () => handleDateChange([dayjs().subtract(6, "day"), dayjs()]),
    },
    {
      key: "this-month",
      label: "This Month",
      onClick: () =>
        handleDateChange([dayjs().startOf("month"), dayjs().endOf("month")]),
    },
    {
      key: "last-month",
      label: "Last Month",
      onClick: () =>
        handleDateChange([
          dayjs().subtract(1, "month").startOf("month"),
          dayjs().subtract(1, "month").endOf("month"),
        ]),
    },
    {
      key: "custom",
      label: (
        <RangePicker
          value={dateRange}
          onChange={handleDateChange}
          format="DD MMM YYYY"
          allowClear
        />
      ),
    },
  ];

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>Welcome to Yogipur</h1>

        {/* Tab Navigation - Top Middle */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === "all" ? "active" : ""}`}
            onClick={() => handleTabChange("all")}
          >
            All
          </button>
          <button
            className={`tab-button ${activeTab === "online" ? "active" : ""}`}
            onClick={() => handleTabChange("online")}
          >
            Online
          </button>
          <button
            className={`tab-button ${activeTab === "offline" ? "active" : ""}`}
            onClick={() => handleTabChange("offline")}
          >
            Offline
          </button>
        </div>

        {/* Date Range Picker */}
        <div className="date-controls">
          {/* <RangePicker
            value={dateRange}
            onChange={handleDateChange}
            allowClear
          /> */}
          <RangePicker
            value={dateRange}
            onChange={handleDateChange}
            format="DD MMM YYYY"
            allowClear
            style={{ width: 350 }}
            placeholder={["Start Date", "End Date"]}
            presets={[
              {
                label: "Today",
                value: [dayjs(), dayjs()],
              },
              {
                label: "Yesterday",
                value: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
              },
              {
                label: "This Week",
                value: [dayjs().startOf("week"), dayjs().endOf("week")],
              },
              {
                label: "Last Week",
                value: [
                  dayjs().subtract(1, "week").startOf("week"),
                  dayjs().subtract(1, "week").endOf("week"),
                ],
              },
              {
                label: "Last 7 Days",
                value: [dayjs().subtract(6, "day"), dayjs()],
              },
              {
                label: "This Month",
                value: [dayjs().startOf("month"), dayjs().endOf("month")],
              },
              {
                label: "Last Month",
                value: [
                  dayjs().subtract(1, "month").startOf("month"),
                  dayjs().subtract(1, "month").endOf("month"),
                ],
              },
              {
                label: "Last 365 Days",
                value: [dayjs().subtract(364, "day"), dayjs()],
              },
            ]}
          />
        </div>
      </header>

      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-info">
            <h3>{dashboardData?.totalClassBooking?.count || 0}</h3>
            <p>Total Class Booking</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <h3>{dashboardData?.totalRevenue?.count || 0}</h3>
            <p>Total Revenue</p>
          </div>
        </div>
      </div>

      <h2>Classes Analytics Last 90 days</h2>

      <div className="charts-grid ">
        <div className="chart-container">
          <h3>Revenue vs Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueChartData}>
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <CartesianGrid strokeDasharray="3 3" />
              <Line type="monotone" dataKey="revenue" stroke="#4bc0c0" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3>Booking Count vs Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={bookingChartData}>
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <CartesianGrid strokeDasharray="3 3" />
              <Line type="monotone" dataKey="bookings" stroke="#3572eb" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {dashboardData?.topBookedClasses && (
          <div className="chart-container">
            <h3>Top Booked Classes</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topClassesData} layout="vertical">
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" />
                <Tooltip />
                <Legend />
                <Bar dataKey="bookings" fill="rgba(75, 192, 192, 0.8)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {dashboardData?.subscribersOverTime && (
          <div className="chart-container">
            <h3>Subscribers Over Time</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={subscribersOverTimeData}>
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <CartesianGrid strokeDasharray="3 3" />
                <Line type="monotone" dataKey="subscribers" stroke="#36a2eb" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="charts-grid ">
        {/* Top Instructors by Booking Count */}
        <div className="chart-container">
          <h3>Top Instructors by Count</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topInstructorsData} layout="vertical">
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" />
              <Tooltip />
              <Legend />
              <Bar dataKey="bookings" fill="#ff6384" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Subscribers Over Time */}
        {dashboardData?.subscribersOverTime && (
          <div className="chart-container">
            <h3>Subscribers Over Time</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={subscribersOverTimeData}>
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <CartesianGrid strokeDasharray="3 3" />
                <Line type="monotone" dataKey="subscribers" stroke="#36a2eb" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Combined Average Metrics Cards */}
      <div className="average-metrics-grid">
        <div className="metric-card">
          <h3>Avg Booking Value</h3>
          <div className="metric-content">
            <p className="value">
              ₹
              {Number(
                dashboardData?.averageBookingValue?.amount || 0
              ).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="metric-card">
          <h3>Avg Class Booking Count</h3>
          <div className="metric-content">
            <p className="value">
              {dashboardData?.averageClassBookingCount || 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
